/**
 * Docs Page - Documentation and help
 */

import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Menu, Close } from '@mui/icons-material';
import AppLayout from '../components/AppLayout';

interface Section {
  id: string;
  title: string;
  icon?: string;
}

const sections: Section[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'main-page', title: 'Main Page: Predictions Table' },
  { id: 'detail-page', title: 'Detail Page' },
  { id: 'tags-review', title: 'Tags Review Tab' },
  { id: 'admin-panel', title: 'Admin Panel' },
  { id: 'workflow-guide', title: 'Workflow Guide' },
  { id: 'glossary', title: 'Glossary' },
];

const drawerWidth = 300;

export default function DocsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const TableOfContents = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', px: 2 }}>
        📑 Contents
      </Typography>
      <List dense>
        {sections.map((section) => (
          <ListItem key={section.id} disablePadding>
            <ListItemButton
              onClick={() => scrollToSection(section.id)}
              selected={activeSection === section.id}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '20',
                  color: theme.palette.primary.main,
                },
              }}
            >
              <ListItemText primary={section.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const drawer = (
    <Box>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Documentation
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <Close />
          </IconButton>
        </Box>
      )}
      <TableOfContents />
    </Box>
  );

  return (
    <AppLayout>
      <Box sx={{ display: 'flex' }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              position: 'fixed',
              top: 80,
              left: 16,
              zIndex: 1300,
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            <Menu />
          </IconButton>
        )}

        {/* Sidebar */}
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileDrawerOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: isMobile ? 'fixed' : 'sticky',
              top: isMobile ? 0 : 64, // Account for AppBar height
              height: isMobile ? '100vh' : 'calc(100vh - 64px)',
              backgroundColor: theme.palette.background.default,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { lg: `calc(100% - ${drawerWidth}px)` },
            ml: isMobile ? 0 : 0,
          }}
        >
          <Container maxWidth="lg" sx={{ py: 4, pl: isMobile ? 8 : 4 }}>
            {/* Title */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                📖 SPINE Tagging App Documentation
              </Typography>
              <Divider sx={{ my: 2 }} />
            </Box>

            {/* Overview */}
            <Box id="overview" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Overview
              </Typography>
              <Typography variant="body1" paragraph>
                The <strong>SPINE Tagging App</strong> is a web application for educational content management and competency
                standards prediction. It provides interfaces for browsing, reviewing, and managing educational
                lessons with associated competency standards, learning targets, and AI-generated predictions.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Key Features
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Browse Lessons</strong>: View all lessons organized by product and experiment version
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Review Predictions</strong>: Evaluate AI-generated competency and limiter predictions
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Align Standards</strong>: View and filter aligned educational standards (Common Core, state standards)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Track Progress</strong>: Monitor review status across lessons
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Admin Tools</strong>: Upload manifests and prediction data
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Architecture
              </Typography>
              <Typography variant="body1" paragraph>
                The application uses a hybrid storage approach:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>DynamoDB</strong>: Stores lesson metadata (manifest data) for fast querying
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>S3</strong>: Stores large prediction data (tag predictions, PDF extractions)
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Getting Started */}
            <Box id="getting-started" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Getting Started
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Authentication
              </Typography>
              <Box component="ol" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Navigate to the application URL
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Enter your email and password
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Click <strong>Login</strong> to access the application
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Navigation
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Predictions Page</strong>: View and filter the predictions table
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Detail Page</strong>: Click any lesson ID to view details and review predictions
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Admin Panel</strong>: Access via the navigation bar (admin users only)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Documentation</strong>: You are here!
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Session
              </Typography>
              <Typography variant="body1" paragraph>
                Your session persists via secure cookies. Use the user menu in the top-right corner to view your
                profile or <strong>Logout</strong> to end your session.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Main Page */}
            <Box id="main-page" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Main Page: Predictions Table
              </Typography>
              <Typography variant="body1" paragraph>
                The main page displays all lesson predictions in a filterable, paginated table.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Selecting Product & Experiment
              </Typography>
              <Typography variant="body1" paragraph>
                Use the dropdowns at the top to select:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Product</strong>: The educational product (e.g., IM_MATH)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Experiment Version</strong>: The prediction model version (e.g., v0.0.0)
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Table Columns
              </Typography>
              <TableContainer component={Paper} sx={{ my: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Column</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell>Lesson identifier (click to view details)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Data</strong></TableCell>
                      <TableCell>✓ = Has prediction data, ✗ = Manifest only</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Lesson Name</strong></TableCell>
                      <TableCell>Human-readable lesson title</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Grade</strong></TableCell>
                      <TableCell>Target grade level</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell>Subject area (e.g., MATHEMATICS)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Unit</strong></TableCell>
                      <TableCell>Unit number within the curriculum</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Target Standards</strong></TableCell>
                      <TableCell>Expected CC standard codes (hover for full list)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Review Status</strong></TableCell>
                      <TableCell>Current review state (color-coded)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Reviewed By</strong></TableCell>
                      <TableCell>Users who have reviewed this lesson</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Review Status Colors
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
                <Chip label="🟡 PENDING: Not yet reviewed" variant="outlined" />
                <Chip label="🔵 IN_PROGRESS: Currently being reviewed" variant="outlined" />
                <Chip label="🟢 APPROVED: Review complete, predictions accepted" variant="outlined" />
                <Chip label="🔴 REJECTED: Review complete, predictions rejected" variant="outlined" />
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Filters
              </Typography>
              <Typography variant="body1" paragraph>
                Use the filter controls to narrow results:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Review Status</strong>: Filter by PENDING, IN_PROGRESS, APPROVED, or REJECTED
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Tag Prediction Type</strong>: Filter by prediction type
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Reviewed By</strong>: Filter by reviewer
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Pagination
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Use <strong>Previous</strong> and <strong>Next</strong> buttons to navigate pages
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Adjust <strong>Records per page</strong> (25, 50, or 100) using the pagination controls
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Detail Page */}
            <Box id="detail-page" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Detail Page
              </Typography>
              <Typography variant="body1" paragraph>
                Click any lesson ID from the main table to open the detail page. This page has two tabs:
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Content Tab
              </Typography>
              <Typography variant="body1" paragraph>
                Displays lesson information and embedded PDF viewer:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Lesson metadata (unit, lesson name, grade, subject)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  PDF document viewer (if available)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Target CC standard codes
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Tags Tab
              </Typography>
              <Typography variant="body1" paragraph>
                The main review interface for evaluating AI predictions. See the next section for details.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Manifest-Only Records
              </Typography>
              <Alert severity="info" sx={{ my: 2 }}>
                If a lesson shows "Manifest Record Only", it means:
                <br />• The lesson metadata exists in DynamoDB
                <br />• Prediction data has not been uploaded to S3 yet
                <br />• The Tags tab will show an informational message instead of the review interface
              </Alert>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Tags Review Tab */}
            <Box id="tags-review" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Tags Review Tab
              </Typography>
              <Typography variant="body1" paragraph>
                The Tags tab provides a three-panel interface for reviewing AI-generated predictions.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Layout Overview
              </Typography>
              <Paper sx={{ p: 2, my: 2, backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
{`┌─────────────┬───────────────────────────┬─────────────┐
│  Left Panel │      Center Chart         │ Right Panel │
│  (Details)  │   (Tags ↔ Standards)      │ (Standards) │
└─────────────┴───────────────────────────┴─────────────┘`}
                </Typography>
              </Paper>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Center Chart: Bipartite Graph
              </Typography>
              <Typography variant="body1" paragraph>
                The chart visualizes connections between competency tags (left) and aligned standards (right).
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Node Colors:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  🔵 <strong>Blue</strong>: Selected/active items
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  ⚪ <strong>Gray</strong>: Unselected items
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  🟡 <strong>Yellow</strong>: Currently focused item
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Interactions:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Click any tag node to focus it and view details in the left panel
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Lines connect tags to their aligned standards
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Left Panel: Tag Details
              </Typography>
              <Typography variant="body1" paragraph>
                When you click a tag in the chart, the left panel shows:
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Competency Section:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Description of the competency
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Confidence score (color-coded: green ≥90%, yellow ≥80%, orange ≥70%, gray &lt;70%)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Hover the ℹ icon to see AI reasoning
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Checkbox to select/deselect the competency
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Limiter Groups:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Expandable groups of limiters that refine the competency
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Each has its own confidence score and reasoning
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Checkbox to select/deselect the group
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Selecting a group enables its child limiters
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Limiters:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Individual limiting factors within each group
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Can be individually selected/deselected
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Deselecting a limiter group automatically deselects all its limiters
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Right Panel: Standards Preview
              </Typography>
              <Typography variant="body1" paragraph>
                Shows aligned educational standards for selected competencies.
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Tabs:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Metrics</strong>: Aggregate statistics (grade coverage, jurisdiction breakdown)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Standards</strong>: Scrollable list of aligned standard cards
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Settings</strong>: Filter controls for the chart and standards display
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Settings Options:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Show non-selected tags in chart
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Filter by Grade Labels
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Filter by Document Code
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Filter by Jurisdiction (NATIONAL, STATE)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Filter by State
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Header Metrics
              </Typography>
              <Typography variant="body1" paragraph>
                Above the chart, you'll see:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Tags</strong>: Selected count / Total count
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Grades</strong>: Visible grades / Total aligned grades
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Saving Changes
              </Typography>
              <Box component="ol" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Make your selections (competencies, limiter groups, limiters)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Set the <strong>Review Status</strong> dropdown (PENDING → IN_PROGRESS → APPROVED/REJECTED)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Click <strong>Save Changes</strong> to persist your review
                </Typography>
              </Box>

              <Alert severity="info" sx={{ my: 2 }}>
                The Save button is only enabled when you have unsaved changes.
              </Alert>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Admin Panel */}
            <Box id="admin-panel" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Admin Panel
              </Typography>
              <Typography variant="body1" paragraph>
                The Admin Panel (accessible from the navigation bar) provides data management tools.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Predictions Table Tab
              </Typography>
              <Typography variant="body1" paragraph>
                Browse and manage all prediction records:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  View records across products and experiments
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Delete records (with confirmation)
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Upload Manifest Tab
              </Typography>
              <Typography variant="body1" paragraph>
                Upload lesson manifest CSV files to create new records:
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Required CSV Columns:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <code>id</code>: Unique lesson identifier (e.g., "R1705.pdf")
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>lesson_name</code>: Human-readable lesson name
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>lesson_number</code>: Lesson number
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>lesson_grade</code>: Grade level (integer)
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>lesson_subject</code>: Subject (e.g., "MATHEMATICS")
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>lesson_pdf</code>: PDF filename
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>unit_number</code>: Unit number
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>unit_name</code>: Unit name
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Optional CSV Columns:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  <code>target_cc_standard_codes</code>: Expected standards (comma-separated, e.g., "8.G.A.1, 8.G.A.2")
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <code>target_standard_ids</code>: Target standard UUIDs (comma-separated, e.g., "uuid1, uuid2")
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                Process:
              </Typography>
              <Box component="ol" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Select Product and Experiment Version
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Select Tag Prediction Type
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Upload your CSV file
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Review the preview and any validation errors
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Click "Upload All Records to Database"
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Upload Tags Predictions Tab
              </Typography>
              <Typography variant="body1" paragraph>
                Upload prediction JSON files to add prediction data to existing manifest records:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Predictions are stored in S3
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Links to existing DynamoDB records by ID
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Workflow Guide */}
            <Box id="workflow-guide" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Workflow Guide
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Typical Review Workflow
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                1. Upload Manifest (Admin)
              </Typography>
              <Paper sx={{ p: 2, my: 1, backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  CSV File → Upload Manifest → DynamoDB Records Created
                </Typography>
              </Paper>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                2. Upload Predictions (Admin)
              </Typography>
              <Paper sx={{ p: 2, my: 1, backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  JSON Predictions → Upload Predictions → S3 Data Stored
                </Typography>
              </Paper>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                3. Review Lessons
              </Typography>
              <Paper sx={{ p: 2, my: 1, backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  Main Page → Select Lesson → Tags Tab → Review & Save
                </Typography>
              </Paper>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Review Process
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                1. Start Review
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Find lessons with status PENDING
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Click the lesson ID to open detail page
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Go to the Tags tab
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                2. Evaluate Predictions
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Click each tag in the chart to review
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Check confidence scores and AI reasoning
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Select/deselect competencies based on accuracy
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Review and adjust limiter groups and limiters
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                3. Check Standards Alignment
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Use the right panel to see aligned standards
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Filter by grade, jurisdiction, or state
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Verify the predicted tags align with expected standards
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                4. Compare with Target Standards
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Check the "Target Standards" column on the main page
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Compare AI predictions with expected CC standard codes
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
                5. Complete Review
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1" paragraph>
                  Set Review Status to APPROVED or REJECTED
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Click Save Changes
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Move to the next lesson
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                Status Progression
              </Typography>
              <Paper sx={{ p: 2, my: 1, backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
{`PENDING → IN_PROGRESS → APPROVED
                     → REJECTED`}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Glossary */}
            <Box id="glossary" sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Glossary
              </Typography>

              <TableContainer component={Paper} sx={{ my: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Term</strong></TableCell>
                      <TableCell><strong>Definition</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Competency</strong></TableCell>
                      <TableCell>A specific skill or knowledge area that a lesson teaches</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Limiter Group</strong></TableCell>
                      <TableCell>A category of constraints that narrow down a competency</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Limiter</strong></TableCell>
                      <TableCell>A specific constraint within a limiter group</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Tag</strong></TableCell>
                      <TableCell>A competency prediction with associated limiter groups</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Standard</strong></TableCell>
                      <TableCell>An educational standard (e.g., Common Core) aligned to competencies</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Manifest</strong></TableCell>
                      <TableCell>Lesson metadata uploaded via CSV</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Prediction</strong></TableCell>
                      <TableCell>AI-generated competency and limiter predictions stored in S3</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Confidence Score</strong></TableCell>
                      <TableCell>AI's certainty about a prediction (0.0 to 1.0)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>SPINE</strong></TableCell>
                      <TableCell>The educational competency framework used for tagging</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell>An educational product line (e.g., IM_MATH)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Experiment</strong></TableCell>
                      <TableCell>A version of the prediction model (e.g., v0.0.0)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>CC Standard Codes</strong></TableCell>
                      <TableCell>Common Core standard identifiers (e.g., 8.G.A.1)</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Alert severity="info" sx={{ my: 4 }}>
              💡 <strong>Tip</strong>: Bookmark this page for quick reference while reviewing lessons.
            </Alert>
          </Container>
        </Box>
      </Box>
    </AppLayout>
  );
}
